// M21 "Ideas" ad family (owner brief 2026-10-12): the creative DEVICES of the big fast-food brands — the
// extreme close-up crop, the oversized product, the giant word, the texture macro, the one-line joke, the
// visual pun — built in the restaurant's own brand. Only the layout ideas are used: no one's ads, slogans,
// characters, logos, colour schemes or artwork. One dish, one moment, one idea; the food huge, one or two
// words, the logo small. The M20 rules hold (tests/unit/templates.test.ts) except: an idea may show no dish
// name and no price, and the bite shot shows the same photo twice.
import type { Builder, Col, TemplateDef, TextOpts } from './dsl';
import { esOnly } from '../ad/fields';

const bi = (es: string, en: string) => ({ es, en });
export type T = Omit<TextOpts, 'role'>;
export const tx = (b: Builder, role: TextOpts['role'], x: number, y: number, w: number, h: number, o: T) =>
  b.text(x, y, w, h, { maxLines: 1, ...o, role, lang: 'es' });

export const SOFT = { blur: 46, y: 28, opacity: 0.24 };

/** Rounded rectangle with a linear gradient running from (x1, y1) to (x2, y2). */
function gradRect(
  b: Builder,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  line: [number, number, number, number],
  stops: [number, Col][],
) {
  const n = b.grad(x, y, w, h, stops);
  if (n.shape.type === 'rect') n.shape.radii = [r, r, r, r];
  const f = n.appearance.fills[0]!;
  if (f.paint.kind === 'linear') f.paint.geom = { x1: line[0], y1: line[1], x2: line[2], y2: line[3] };
  return n;
}

/**
 * A modern phone for scale, in neutrals only: a soft contact shadow, the body with a metal edge, side buttons,
 * a dark screen with a diagonal glare, and the camera island. (x, y) is the top-left, w the body width.
 */
function phone(b: Builder, x: number, y: number, w: number) {
  const h = w * 2.05,
    r = w * 0.17;
  // Contact shadow on the table.
  b.glow(x - w * 0.25, y + h - w * 0.12, w * 1.5, w * 0.36, x + w / 2, y + h + w * 0.04, w * 0.7, [
    [0, ['black', 0.35]],
    [1, ['black', 0]],
  ]);
  // Side buttons.
  b.rect(x - 4, y + h * 0.2, 8, h * 0.08, { fill: '#3a3a3a', r: 3 });
  b.rect(x - 4, y + h * 0.31, 8, h * 0.08, { fill: '#3a3a3a', r: 3 });
  b.rect(x + w - 4, y + h * 0.26, 8, h * 0.13, { fill: '#3a3a3a', r: 3 });
  // Body: brushed-metal edge, then the black glass.
  gradRect(
    b,
    x,
    y,
    w,
    h,
    r,
    [x, y, x + w, y + h],
    [
      [0, '#8a8a8a'],
      [0.5, '#4a4a4a'],
      [1, '#9a9a9a'],
    ],
  );
  const e = w * 0.035;
  b.rect(x + e, y + e, w - 2 * e, h - 2 * e, { fill: '#0e0e0e', r: r - e });
  // Screen with a soft top-to-bottom sheen, then a diagonal glare.
  const s = w * 0.07;
  const sx = x + s,
    sy = y + s,
    sw = w - 2 * s,
    sh = h - 2 * s,
    sr = r - s;
  gradRect(
    b,
    sx,
    sy,
    sw,
    sh,
    sr,
    [sx, sy, sx, sy + sh],
    [
      [0, '#2b2b2b'],
      [1, '#141414'],
    ],
  );
  gradRect(
    b,
    sx,
    sy,
    sw,
    sh,
    sr,
    [sx, sy, sx + sw, sy + sh * 0.6],
    [
      [0, ['white', 0]],
      [0.32, ['white', 0]],
      [0.4, ['white', 0.22]],
      [0.52, ['white', 0.05]],
      [0.6, ['white', 0]],
      [1, ['white', 0]],
    ],
  );
  // Camera island.
  b.rect(x + w / 2 - w * 0.14, sy + w * 0.05, w * 0.28, w * 0.075, { fill: '#000000', r: w * 0.04 });
}
export const DEEP = { blur: 70, y: 40, opacity: 0.55 };

export const IDEAS_TEMPLATES: TemplateDef[] = [
  // 1. Extreme close-up: the dish fills the frame; only the price and a small logo.
  {
    id: 'ideas-closeup',
    family: 'ideas',
    slot: 'full',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Ideas — primer plano extremo', 'Ideas — extreme close-up'),
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 1350, { zoom: 2.2, offsetY: 0.08 });
      b.rect(0, 1160, 440, 190, { fill: 'primary' });
      tx(b, 'price', 60, 1190, 360, 130, {
        family: 'Anton',
        size: 104,
        color: 'onPrimary',
        valign: 'middle',
      });
      b.logo(920, 1190, 130, 130);
    },
  },
  // 2. Crop puzzle: a corner of the dish on flat orange, one tiny word.
  {
    id: 'ideas-crop',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — rompecabezas', 'Ideas — crop puzzle'),
    ad: { headline: esOnly('Adivine.') },
    build: (b) => {
      b.bg('primary');
      b.photo(0, 0, 1080, 1080, { fit: 'contain', zoom: 1.45, offsetX: 0.42, offsetY: 0.4, shadow: DEEP });
      tx(b, 'headline', 70, 70, 500, 50, {
        family: 'Montserrat',
        weight: 700,
        size: 36,
        color: 'onPrimarySmall',
        tracking: 2,
      });
      b.logo(70, 920, 110, 110);
    },
  },
  // 3. Oversized product: the dish bleeds off two edges under one bold condensed headline.
  {
    id: 'ideas-oversized',
    family: 'ideas',
    slot: 'cutout',
    category: 'story',
    format: 'story',
    name: bi('Ideas — producto gigante', 'Ideas — oversized product'),
    ad: { headline: esOnly('Más grande. Punto.') },
    adAny: { headline: esOnly('Grande como su antojo.') },
    build: (b) => {
      b.bg('secondary');
      tx(b, 'headline', 60, 290, 800, 400, {
        family: 'Anton',
        size: 190,
        color: 'onSecondary',
        caps: true,
        maxLines: 2,
        leading: 0.98,
      });
      b.photo(0, 0, 1080, 1920, { fit: 'contain', zoom: 1.6, offsetX: 0.3, offsetY: 0.4, shadow: DEEP });
      b.logo(890, 290, 130, 130);
    },
  },
  // 4. Size proof: the dish beside a phone, one line.
  {
    id: 'ideas-size',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — prueba de tamaño', 'Ideas — size proof'),
    ad: { headline: esOnly('Más grande que su celular.') },
    adAny: { headline: esOnly('Más grande de lo que se imagina.') },
    build: (b) => {
      b.bg('white');
      tx(b, 'headline', 60, 60, 960, 80, {
        family: 'Oswald',
        weight: 600,
        size: 62,
        color: 'accentText',
      });
      b.photo(30, 170, 720, 820, { fit: 'contain', zoom: 0.95, shadow: SOFT });
      phone(b, 800, 590, 170);
      b.logo(900, 40, 140, 140, { onLight: true });
    },
  },
  // 5. Giant word: one huge word; the dish breaks through the letters.
  {
    id: 'ideas-word',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Ideas — palabra gigante', 'Ideas — giant word'),
    ad: { headline: esOnly('¿HAMBRE?') },
    build: (b) => {
      b.bg('secondary');
      tx(b, 'headline', 30, 110, 1020, 400, {
        family: 'Anton',
        size: 330,
        color: 'accentOnSecondary',
        align: 'center',
        caps: true,
        leading: 1,
      });
      b.photo(90, 380, 900, 830, { fit: 'contain', zoom: 1, shadow: DEEP });
      tx(b, 'price', 60, 1220, 560, 96, {
        family: 'Oswald',
        weight: 700,
        size: 80,
        color: 'onSecondary',
      });
      b.logo(900, 1190, 130, 130);
    },
  },
  // 6. Texture macro: the crispiest part, very close, warm; one word.
  {
    id: 'ideas-texture',
    family: 'ideas',
    slot: 'full',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — textura de cerca', 'Ideas — texture macro'),
    ad: { headline: esOnly('Crujiente.') },
    adAny: { headline: esOnly('Antojo resuelto.') },
    build: (b) => {
      b.bg('secondary');
      b.photo(0, 0, 1080, 1080, { zoom: 3, offsetX: 0.1, offsetY: 0.12 });
      b.grad(0, 700, 1080, 380, [
        [0, ['secondary', 0]],
        [1, ['secondary', 0.8]],
      ]);
      tx(b, 'headline', 70, 900, 800, 130, { family: 'Anton', size: 120, color: 'white' });
      b.logo(920, 40, 120, 120);
    },
  },
  // 7. Cheeky one-liner: solid brown, a white condensed joke, a small dish, the price.
  {
    id: 'ideas-joke',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — una línea con humor', 'Ideas — cheeky one-liner'),
    ad: { headline: esOnly('Su dieta puede esperar. Esta no.') },
    build: (b) => {
      b.bg('secondary');
      tx(b, 'headline', 70, 80, 940, 330, {
        family: 'Oswald',
        weight: 700,
        size: 100,
        color: 'onSecondary',
        caps: true,
        maxLines: 3,
        leading: 1.04,
      });
      b.photo(420, 420, 640, 640, { fit: 'contain', zoom: 0.92, shadow: DEEP });
      tx(b, 'price', 70, 900, 380, 100, {
        family: 'Oswald',
        weight: 700,
        size: 84,
        color: 'accentOnSecondary',
      });
      b.logo(70, 700, 130, 130);
    },
  },
  // 8. Visual pun: the dish rises like the sun over the horizon.
  {
    id: 'ideas-pun',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-square',
    name: bi('Ideas — juego visual', 'Ideas — visual pun'),
    ad: { headline: esOnly('Amanece con hambre.') },
    adAny: { headline: esOnly('Póngale sabor a su día.') },
    build: (b) => {
      b.bg('primary');
      // Sun rays (plain lines).
      for (let i = 0; i < 13; i++) {
        const a = Math.PI + (i * Math.PI) / 12;
        const cx = 540,
          cy = 700;
        b.line(
          cx + Math.cos(a) * 400,
          cy + Math.sin(a) * 400,
          cx + Math.cos(a) * 520,
          cy + Math.sin(a) * 520,
          ['white', 0.6],
          10,
        );
      }
      b.photo(220, 340, 640, 640, { fit: 'contain', zoom: 0.95, shadow: SOFT });
      b.rect(0, 700, 1080, 380, { fill: 'secondary' });
      tx(b, 'headline', 70, 780, 940, 90, {
        family: 'Oswald',
        weight: 600,
        size: 66,
        color: 'onSecondarySmall',
      });
      tx(b, 'price', 70, 900, 420, 100, {
        family: 'Oswald',
        weight: 700,
        size: 80,
        color: 'accentOnSecondary',
      });
      b.logo(910, 40, 130, 130);
    },
  },
  // 9. Brand flood: all orange, the dish oversized, the price in brown.
  {
    id: 'ideas-flood',
    family: 'ideas',
    slot: 'cutout',
    category: 'post',
    format: 'ig-portrait',
    name: bi('Ideas — todo naranja', 'Ideas — brand flood'),
    build: (b) => {
      b.bg('primary');
      tx(b, 'price', 60, 60, 700, 190, { family: 'Anton', size: 170, color: 'secondary' });
      b.photo(0, 220, 1080, 1130, { fit: 'contain', zoom: 1.35, offsetX: 0.16, offsetY: 0.14, shadow: DEEP });
      b.logo(910, 50, 130, 130);
    },
  },
  // 10. Countdown / limited: "Solo hoy", the dish huge, the old price struck through.
  {
    id: 'ideas-today',
    family: 'ideas',
    slot: 'cutout',
    category: 'promo',
    format: 'story',
    name: bi('Ideas — solo hoy', 'Ideas — today only'),
    ad: { headline: esOnly('Solo hoy') },
    build: (b) => {
      b.bg('primary');
      // M21 review: the dish about twice as big, bleeding off the sides and the bottom.
      b.photo(0, 540, 1080, 1380, {
        fit: 'contain',
        zoom: 1.92,
        offsetX: 0.08,
        offsetY: 0.12,
        shadow: DEEP,
      });
      tx(b, 'headline', 60, 290, 780, 240, {
        family: 'Anton',
        size: 220,
        color: 'onPrimary',
        caps: true,
        leading: 1,
      });
      b.logo(890, 290, 130, 130);
      b.rect(60, 1300, 460, 236, { fill: 'secondary' });
      tx(b, 'oldPrice', 60, 1316, 460, 60, {
        family: 'Montserrat',
        weight: 600,
        size: 44,
        color: 'onSecondarySmall',
        align: 'center',
      });
      b.line(195, 1346, 385, 1346, 'onSecondarySmall', 4);
      tx(b, 'price', 60, 1384, 460, 136, {
        family: 'Anton',
        size: 110,
        color: 'onSecondary',
        align: 'center',
        valign: 'middle',
      });
    },
  },
  // 11. Bite shot: the whole dish and a bitten one, side by side (the same photo).
  {
    id: 'ideas-bite',
    family: 'ideas',
    slot: 'cutout',
    category: 'delivery',
    format: 'delivery',
    name: bi('Ideas — antes y después de la mordida', 'Ideas — bite shot'),
    ad: { headline: esOnly('Una mordida y ya no para.') },
    build: (b) => {
      b.bg('white');
      tx(b, 'headline', 50, 30, 900, 70, {
        family: 'Oswald',
        weight: 600,
        size: 54,
        color: 'accentText',
      });
      b.photo(20, 110, 570, 610, { fit: 'contain', zoom: 0.92, shadow: SOFT });
      b.photo(610, 110, 570, 610, { fit: 'contain', zoom: 0.92, shadow: SOFT, bite: 0.15 });
      tx(b, 'price', 820, 716, 340, 76, {
        family: 'Oswald',
        weight: 700,
        size: 64,
        color: 'accentText',
        align: 'right',
      });
      b.logo(1070, 14, 110, 110, { onLight: true });
    },
  },
  // 12. Night craving: dark brown, the dish under a spotlight.
  {
    id: 'ideas-night',
    family: 'ideas',
    slot: 'cutout',
    category: 'story',
    format: 'story',
    name: bi('Ideas — antojo de noche', 'Ideas — night craving'),
    ad: { headline: esOnly('¿Antojo de noche?') },
    build: (b) => {
      b.bg('secondary');
      b.glow(0, 0, 1080, 1920, 600, 1330, 900, [
        [0, ['white', 0.26]],
        [0.55, ['white', 0.08]],
        [1, ['white', 0]],
      ]);
      tx(b, 'headline', 60, 290, 800, 240, {
        family: 'Oswald',
        weight: 700,
        size: 104,
        color: 'onSecondary',
        maxLines: 2,
        leading: 1.05,
      });
      // M21 review: the dish about twice as big, bleeding off the sides and the bottom.
      b.photo(0, 560, 1080, 1360, {
        fit: 'contain',
        zoom: 1.44,
        offsetX: 0.06,
        offsetY: 0.1,
        shadow: DEEP,
      });
      b.logo(890, 290, 130, 130);
      b.rect(60, 1396, 440, 140, { fill: 'secondary' });
      tx(b, 'price', 60, 1410, 440, 110, {
        family: 'Oswald',
        weight: 700,
        size: 92,
        color: 'accentOnSecondary',
        align: 'center',
      });
    },
  },
];
